import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { IssueStatus, PMIssueStatus } from "@/domains/pm/types";
import { listIssueStatuses } from "@/stores/pm/repository";
import { useStatusCatalogue } from "./statusCatalogueContext";

/**
 * The create / edit / delete state every status dropdown shares.
 *
 * The catalogue holds descriptors — what a status LOOKS like — but editing one
 * needs its row id and its stored English label, which a descriptor
 * deliberately does not carry (it holds the label already resolved into the
 * current language, and writing that back would overwrite the English name
 * with its Estonian translation).
 */
export function useCustomStatusRow(buildingProjectId: number | null) {
  const { buildingId } = useStatusCatalogue();
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [targetKey, setTargetKey] = useState<IssueStatus | null>(null);

  // Same query key as the catalogue's, so this is the cached rows, not a
  // second fetch.
  const { data: rows } = useQuery({
    queryKey: ["pm-issue-statuses", buildingId],
    queryFn: () => listIssueStatuses(buildingId as number),
    enabled: buildingId !== undefined,
    staleTime: 60_000,
  });

  const rowFor = useCallback(
    (key: IssueStatus | null): PMIssueStatus | null => {
      if (!key || buildingProjectId == null) return null;
      return (
        (rows ?? []).find((r) => r.building_project_id === buildingProjectId && r.key === key) ??
        null
      );
    },
    [rows, buildingProjectId]
  );

  const startCreate = useCallback(() => {
    setTargetKey(null);
    setFormOpen(true);
  }, []);

  const startEdit = useCallback((key: IssueStatus) => {
    setTargetKey(key);
    setFormOpen(true);
  }, []);

  const startDelete = useCallback((key: IssueStatus) => {
    setTargetKey(key);
    setDeleteOpen(true);
  }, []);

  return useMemo(
    () => ({
      buildingProjectId,
      formOpen,
      setFormOpen,
      deleteOpen,
      setDeleteOpen,
      editingRow: formOpen ? rowFor(targetKey) : null,
      deletingRow: deleteOpen ? rowFor(targetKey) : null,
      startCreate,
      startEdit,
      startDelete,
    }),
    [
      buildingProjectId,
      formOpen,
      deleteOpen,
      rowFor,
      targetKey,
      startCreate,
      startEdit,
      startDelete,
    ]
  );
}
